import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { grey, green, red } from '@mui/material/colors';

import api from '../../../services/api';

export default function SecuritySettings() {
  const [devices, setDevices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = React.useState(null);
  const [deleteDialog, setDeleteDialog] = React.useState({ open: false, deviceId: null, deviceName: '' });
  const [deleteAllDialog, setDeleteAllDialog] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesRes, statusRes] = await Promise.all([
        api.get('/admin/auth/devices/'),
        api.get('/admin/auth/2fa/status')
      ]);
      setDevices(devicesRes.data.devices || []);
      setTwoFactorStatus(statusRes.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao carregar dados', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/admin/auth/devices/${deleteDialog.deviceId}`);
      setSnackbar({ open: true, message: 'Dispositivo removido com sucesso', severity: 'success' });
      setDeleteDialog({ open: false, deviceId: null, deviceName: '' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao remover dispositivo', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAllDevices = async () => {
    setActionLoading(true);
    try {
      const response = await api.delete('/admin/auth/devices/all');
      setSnackbar({
        open: true,
        message: `${response.data.removed_count} dispositivo(s) removido(s)`,
        severity: 'success'
      });
      setDeleteAllDialog(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao remover dispositivos', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ mr: 1 }} />
        Seguranca da Conta
      </Typography>

      {/* Status do 2FA */}
      <Card sx={{ mb: 3, bgcolor: 'white' }}>
        <CardHeader
          title="Autenticacao em Duas Etapas (2FA)"
          subheader="Proteja sua conta com verificacao por email"
        />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ color: green[500], mr: 1 }} />
              <Typography>
                2FA esta <strong>ativo</strong> e <strong>obrigatorio</strong> para administradores
              </Typography>
            </Box>
            <Chip
              label="Sempre Ativo"
              color="success"
              size="small"
            />
          </Box>
          {twoFactorStatus && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {twoFactorStatus.trusted_devices_count} dispositivo(s) confiavel(is)
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Lista de Dispositivos Confiaveis */}
      <Card sx={{ bgcolor: 'white' }}>
        <CardHeader
          title="Dispositivos Confiaveis"
          subheader="Dispositivos que nao precisam de codigo 2FA para login"
          action={
            devices.length > 0 && (
              <Button
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setDeleteAllDialog(true)}
                size="small"
              >
                Remover Todos
              </Button>
            )
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          {devices.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <ComputerIcon sx={{ fontSize: 48, color: grey[400], mb: 1 }} />
              <Typography color="text.secondary">
                Nenhum dispositivo confiavel registrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Marque "Confiar neste dispositivo" no proximo login para adicionar
              </Typography>
            </Box>
          ) : (
            <List>
              {devices.map((device, index) => (
                <React.Fragment key={device.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon>
                      <ComputerIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={device.device_name}
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" component="span" color="text.secondary">
                            IP: {device.ip || 'Desconhecido'}
                          </Typography>
                          <br />
                          <Typography variant="body2" component="span" color="text.secondary">
                            <AccessTimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Ultimo acesso: {formatDate(device.last_used)}
                          </Typography>
                          <br />
                          <Typography variant="body2" component="span" color="text.secondary">
                            Expira em: {device.expires_in_days} dias
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="remover"
                        onClick={() => setDeleteDialog({
                          open: true,
                          deviceId: device.id,
                          deviceName: device.device_name
                        })}
                        sx={{ color: red[500] }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmacao para remover um dispositivo */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, deviceId: null, deviceName: '' })}
      >
        <DialogTitle>Remover Dispositivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o dispositivo "{deleteDialog.deviceName}"?
            O proximo login deste dispositivo exigira verificacao por codigo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, deviceId: null, deviceName: '' })}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteDevice}
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmacao para remover todos */}
      <Dialog
        open={deleteAllDialog}
        onClose={() => setDeleteAllDialog(false)}
      >
        <DialogTitle>Remover Todos os Dispositivos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover TODOS os dispositivos confiaveis?
            Todos os proximos logins exigirao verificacao por codigo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialog(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteAllDevices} color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Remover Todos'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
